import React from 'react';
import { 
  Droplet, Activity, ShieldCheck, Settings, Target, Eye, 
  Droplets, Factory, RefreshCw, BarChart, Wrench, MapPin, Calendar, ArrowRight 
} from 'lucide-react';

export default function Home() {
  return (
    <div className="animate-fade-in">
      
      {/* 1. HERO SECTION */}
      <section id="home" className="bg-gradient-to-br from-blue-900 to-slate-800 text-white py-32 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            Integrated Water Management Infrastructure
          </h1>
          <p className="text-xl md:text-2xl text-blue-200 mb-10 max-w-3xl mx-auto font-light">
            Providing end-to-end solutions in developing and implementing sustainable water and wastewater projects across India.
          </p>
          <a href="/#projects" className="inline-block bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-md font-semibold transition">
            Explore Our Projects
          </a>
        </div>
      </section>

      {/* 2. ABOUT US SECTION */}
      <section id="about" className="py-24 px-6 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Who We Are</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto rounded"></div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-12 items-center mb-16">
            <div className="md:w-1/2">
              <div className="space-y-4 text-slate-600 leading-relaxed text-lg">
                <p>
                  IL&FS Paradip Refinery Water Limited (IPRWL), born from the legacy of IL&FS Water Limited (IWL), is a premier infrastructure development company dedicated to the water and wastewater sectors. 
                </p>
                <p>
                  From concept to execution, we specialize in project conceptualization, structuring, financing, and implementation. Our expertise lies in executing complex, large-scale projects on a Public-Private Partnership (PPP) and Build-Own-Operate-Transfer (BOOT) basis.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 grid grid-cols-2 gap-4">
              <img src={`${process.env.PUBLIC_URL}/img/ipr.avif`} alt="Water Plant" className="rounded-lg shadow-md w-full h-48 object-cover" />
              <img src={`${process.env.PUBLIC_URL}/img/ipr1.avif`} alt="Infrastructure" className="rounded-lg shadow-md w-full h-48 object-cover" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="bg-slate-50 p-10 rounded-2xl border border-slate-100">
              <Target className="text-blue-600 w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600">
                To deliver commercially viable, sustainable water and environmental infrastructure solutions that create value for stakeholders while safeguarding natural resources.
              </p>
            </div>
            <div className="bg-slate-50 p-10 rounded-2xl border border-slate-100">
              <Eye className="text-blue-600 w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
              <p className="text-slate-600">
                To be the premier partner in India for water infrastructure development, recognized for technological innovation, operational excellence, and commitment to quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SERVICES SECTION */}
      <section id="services" className="py-24 px-6 bg-slate-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Expertise Across the Value Chain</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto rounded mb-6"></div>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We provide the technical and financial muscle to bring critical water projects to life.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <BarChart className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Project Structuring</h3>
              <p className="text-slate-600 text-sm">End-to-end conceptualization and financial structuring. We specialize in PPP, BOOT, and EPC frameworks.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <Droplets className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Desalination Plants</h3>
              <p className="text-slate-600 text-sm">Design and implementation of large-scale Sea Water Reverse Osmosis (SWRO) plants for coastal demands.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <RefreshCw className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Wastewater Recycling</h3>
              <p className="text-slate-600 text-sm">Development of STPs and ETPs focusing on zero liquid discharge and maximum resource recovery.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <Factory className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Industrial Solutions</h3>
              <p className="text-slate-600 text-sm">Customized water treatment solutions for the rigorous demands of refineries and heavy manufacturing.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <Wrench className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Transmission Systems</h3>
              <p className="text-slate-600 text-sm">Engineering and execution of robust pipeline networks, pumping stations, and distribution infrastructure.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
              <Settings className="w-8 h-8 text-blue-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">Operations & Maintenance</h3>
              <p className="text-slate-600 text-sm">Lifecycle management ensuring optimal plant efficiency, regulatory compliance, and reduced downtime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PROJECTS SECTION */}
      <section id="projects" className="py-24 px-6 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Featured Projects</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto rounded"></div>
          </div>
          
          <div className="space-y-12">
            
            {/* Project 1 */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 flex flex-col md:flex-row">
              <div className="md:w-2/5 h-64 md:h-auto overflow-hidden">
                <img src={`${process.env.PUBLIC_URL}/img/ipr.avif`} alt="Paradip Refinery" className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 md:w-3/5 flex flex-col justify-center">
                <span className="text-blue-600 font-semibold text-sm mb-2 uppercase tracking-wider">Desalination & Transmission</span>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Paradip Refinery Water Project</h3>
                <p className="text-slate-600 mb-6">Development of a large-scale water supply and treatment system to meet the rigorous industrial requirements of the Paradip Refinery, ensuring continuous and reliable operations.</p>
                <div className="flex gap-6 text-sm text-slate-500 font-medium">
                  <span className="flex items-center"><MapPin className="w-4 h-4 mr-1"/> Paradip, Odisha</span>
                  <span className="flex items-center"><Calendar className="w-4 h-4 mr-1"/> Operational</span>
                </div>
              </div>
            </div>

            {/* Project 2 */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 flex flex-col md:flex-row-reverse">
              <div className="md:w-2/5 h-64 md:h-auto overflow-hidden">
                <img src={`${process.env.PUBLIC_URL}/img/ipr1.avif`} alt="Tirupur ZLD" className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 md:w-3/5 flex flex-col justify-center">
                <span className="text-blue-600 font-semibold text-sm mb-2 uppercase tracking-wider">Wastewater & Recycling</span>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Tirupur Zero Liquid Discharge Plant</h3>
                <p className="text-slate-600 mb-6">Implementation of a state-of-the-art ZLD facility for the textile cluster, treating heavily polluted effluent and recovering high-quality water for reuse.</p>
                <div className="flex gap-6 text-sm text-slate-500 font-medium">
                  <span className="flex items-center"><MapPin className="w-4 h-4 mr-1"/> Tirupur, Tamil Nadu</span>
                  <span className="flex items-center"><Calendar className="w-4 h-4 mr-1"/> Completed</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}